import { auth } from '../firebase';
import { countPendingOfflineSales } from './offlineSalesQueue';
import { enqueueOfflineOperation, listOfflineOperations, OFFLINE_OPERATION_TYPE } from './offlineOperationsQueue';
import { canOpenCashOffline, getOfflineDeviceId } from './offlineDevice';

const ACTIVE_OFFLINE_CASH_KEY = 'valenpos.activeOfflineCash.v1';

const cacheOfflineCash = (cash) => {
  if (typeof window === 'undefined' || !cash?.id) return;
  window.localStorage.setItem(ACTIVE_OFFLINE_CASH_KEY, JSON.stringify(cash));
};

export const clearCachedOfflineCash = () => {
  if (typeof window !== 'undefined') window.localStorage.removeItem(ACTIVE_OFFLINE_CASH_KEY);
};

const readCachedOfflineCash = () => {
  if (typeof window === 'undefined') return null;
  try { return JSON.parse(window.localStorage.getItem(ACTIVE_OFFLINE_CASH_KEY) || 'null'); }
  catch (_error) { return null; }
};

export class CashApiError extends Error {
  constructor(message, code = 'CASH_FAILED', status = 0, data = null) {
    super(message);
    this.name = 'CashApiError';
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

const parseApiJson = async (res) => {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (_error) {
    return { error: text || 'Respuesta inválida del servidor' };
  }
};

async function postCashApi(path, payload) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new CashApiError('No se pudo obtener la sesión. Vuelve a iniciar sesión.', 'AUTH_REQUIRED');
  }

  let res;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  } catch (_error) {
    throw new CashApiError('No se pudo comunicar con el servidor de caja.', 'CASH_NETWORK_ERROR');
  }
  const data = await parseApiJson(res);
  if (!res.ok) {
    throw new CashApiError(
      data?.error || 'No se pudo completar la operación de caja',
      data?.code || 'CASH_FAILED',
      res.status,
      data
    );
  }
  return data;
}

export async function getOpenCashStatus() {
  const data = await postCashApi('/api/cash/status', {});
  const cash = data?.cash || null;
  if (!cash) clearCachedOfflineCash();
  else if (cash.openedOffline === true && cash.offlineDeviceId === getOfflineDeviceId()) cacheOfflineCash(cash);
  return cash;
}

export async function openCashRegister(_user, amount, options = {}) {
  try {
    const data = await postCashApi('/api/cash/open', { amount: Number(amount), ...options });
    return data?.cash || null;
  } catch (error) {
    if (error?.code === 'CASH_ALREADY_OPEN' && error?.data?.cash) {
      const expectedCashId = options?.offlineOpen?.provisionalCashId;
      if (!expectedCashId || String(error.data.cash.id) === String(expectedCashId)) return error.data.cash;
    }
    throw error;
  }
}

export async function openAssignedCashRegister({ targetUid, sourceCashId, amount }) {
  const data = await postCashApi('/api/cash/open', {
    targetUid,
    sourceCashId,
    amount: Number(amount),
  });
  return data?.cash || null;
}

export async function closeCashRegister(cashId, closedTurnData, { administrativeClose = false } = {}) {
  let pendingOfflineSales = 0;
  try {
    pendingOfflineSales = await countPendingOfflineSales(cashId);
  } catch (error) {
    throw new CashApiError(
      'No se pudo verificar si existen ventas offline pendientes en este dispositivo. Recarga la aplicación antes de cerrar.',
      'CASH_OFFLINE_QUEUE_UNAVAILABLE',
      0,
      { cause: error?.message || '' }
    );
  }
  if (pendingOfflineSales > 0) {
    throw new CashApiError(
      `No puedes cerrar la caja: hay ${pendingOfflineSales} venta${pendingOfflineSales === 1 ? '' : 's'} offline pendiente${pendingOfflineSales === 1 ? '' : 's'} de sincronizar en este dispositivo.`,
      'CASH_OFFLINE_SALES_PENDING',
      409,
      { pendingOfflineSales }
    );
  }
  const pendingCashOperations = (await listOfflineOperations()).filter(
    (operation) => String(operation.cashId || '') === String(cashId || '')
  );
  if (pendingCashOperations.length > 0) {
    throw new CashApiError(
      'No puedes cerrar la caja hasta que su apertura y todas sus operaciones offline estén sincronizadas.',
      'CASH_OFFLINE_OPERATIONS_PENDING',
      409,
      { pendingOperations: pendingCashOperations.length }
    );
  }
  const data = await postCashApi('/api/cash/close', {
    cashId,
    closedTurnData,
    administrativeClose,
    deviceId: getOfflineDeviceId(),
  });
  clearCachedOfflineCash();
  return data?.cash || { id: cashId };
}

export async function deleteCashClose(cashId, reason) {
  return postCashApi('/api/cash/close', { action: 'deleteClose', cashId, reason });
}

const localId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const bogotaDate = (date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

export async function openCashRegisterOfflineAware({ currentUser, amount, isOnline, storeId = 'SUC001' }) {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    throw new CashApiError('El monto de apertura debe ser mayor a cero.', 'CASH_AMOUNT_INVALID', 400);
  }

  const offlineAuthorized = canOpenCashOffline(currentUser, storeId);
  if (isOnline && !offlineAuthorized) return openCashRegister(currentUser, numericAmount);
  if (!offlineAuthorized) {
    throw new CashApiError(
      'Este computador no está autorizado como PC principal. Conéctalo a internet y pide a un administrador autorizarlo en Configuración.',
      'OFFLINE_DEVICE_NOT_AUTHORIZED',
      403
    );
  }

  const existingPendingCash = await getPendingOfflineCashOpen(currentUser?.uid);
  if (existingPendingCash?.id) return existingPendingCash;

  const now = new Date();
  const cashId = `cash_offline_${localId()}`;
  const operationId = `cash-open-${cashId}`;
  const reconcileOperationId = `cash-reconcile-${cashId}`;
  const cash = {
    id: cashId,
    user: currentUser?.username || currentUser?.email || '',
    userUid: currentUser?.uid || null,
    fecha: bogotaDate(now),
    hora: now.toLocaleTimeString('es-CO'),
    montoApertura: numericAmount,
    amount: numericAmount,
    estado: 'abierta',
    status: 'abierta',
    storeId,
    openedAt: now.toISOString(),
    openedByUid: currentUser?.uid || null,
    openedByEmail: currentUser?.email || null,
    offlineProvisional: true,
    offlineOpenOperationId: operationId,
    offlineReconcileOperationId: reconcileOperationId,
    deviceId: getOfflineDeviceId(),
    schemaVersion: 2,
  };
  await enqueueOfflineOperation({
    id: operationId,
    type: OFFLINE_OPERATION_TYPE.CASH_OPEN,
    priority: 10,
    storeId,
    cashId,
    ownerUid: currentUser?.uid || null,
    createdAt: now.getTime(),
    occurredAt: now.getTime(),
    payload: {
      amount: numericAmount,
      provisionalCashId: cashId,
      operationId,
      deviceId: cash.deviceId,
      openedOfflineAt: now.toISOString(),
      businessDate: cash.fecha,
      storeId,
    },
  });
  await enqueueOfflineOperation({
    id: reconcileOperationId,
    type: OFFLINE_OPERATION_TYPE.CASH_RECONCILE,
    priority: 90,
    storeId,
    cashId,
    ownerUid: currentUser?.uid || null,
    createdAt: now.getTime() + 1,
    occurredAt: now.getTime(),
    dependsOn: [operationId],
    payload: { cashId, deviceId: cash.deviceId, storeId, cashSnapshot: cash },
  });
  cacheOfflineCash(cash);
  return cash;
}

export async function syncOfflineCashOpen(operation) {
  const payload = operation?.payload || {};
  return openCashRegister(null, payload.amount, {
    storeId: payload.storeId || operation.storeId,
    offlineOpen: {
      operationId: payload.operationId || operation.id,
      provisionalCashId: payload.provisionalCashId || operation.cashId,
      deviceId: payload.deviceId,
      openedOfflineAt: payload.openedOfflineAt,
      businessDate: payload.businessDate,
    },
  });
}

export async function syncOfflineCashReconcile(operation) {
  const payload = operation?.payload || {};
  const data = await postCashApi('/api/cash/open', {
    action: 'offlineCashReconcile',
    cashId: payload.cashId || operation.cashId,
    deviceId: payload.deviceId,
    storeId: payload.storeId || operation.storeId,
  });
  return data?.cash || null;
}

export async function getPendingOfflineCashOpen(ownerUid) {
  const operations = await listOfflineOperations();
  const operation = operations.find((record) => (
    record.type === OFFLINE_OPERATION_TYPE.CASH_OPEN
    && (!ownerUid || record.ownerUid === ownerUid)
  ));
  const reconcileOperation = operations.find((record) => (
    record.type === OFFLINE_OPERATION_TYPE.CASH_RECONCILE
    && (!ownerUid || record.ownerUid === ownerUid)
  ));
  if (!operation) {
    const queuedSnapshot = reconcileOperation?.payload?.cashSnapshot;
    const cached = queuedSnapshot?.id ? queuedSnapshot : readCachedOfflineCash();
    if (!cached?.id || (ownerUid && cached.userUid !== ownerUid && cached.openedByUid !== ownerUid)) return null;
    return cached;
  }
  const payload = operation.payload || {};
  return {
    id: payload.provisionalCashId || operation.cashId,
    userUid: operation.ownerUid || ownerUid || null,
    fecha: payload.businessDate,
    montoApertura: Number(payload.amount || 0),
    amount: Number(payload.amount || 0),
    estado: 'abierta',
    status: 'abierta',
    storeId: payload.storeId || operation.storeId || 'SUC001',
    openedAt: payload.openedOfflineAt,
    offlineProvisional: true,
    offlineOpenOperationId: operation.id,
    offlineReconcileOperationId: `cash-reconcile-${payload.provisionalCashId || operation.cashId}`,
    deviceId: payload.deviceId || null,
  };
}

export async function editCashClose(cashId, { finalAmount, observations, reason }) {
  const data = await postCashApi('/api/cash/close', {
    action: 'editClose',
    cashId,
    finalAmount: Number(finalAmount),
    observations,
    reason,
  });
  return data?.correction || null;
}

export async function voidCashReturn(cashId, transferId, reason) {
  return postCashApi('/api/cash/close', {
    action: 'voidReturn',
    cashId,
    transferId,
    reason,
  });
}
